/**
 * CreateUserTemplate — metadata-driven layout for a person's shared profile.
 *
 * Purpose: Uses the same FormTemplate pattern as CreateMediaPlanTemplate: explicit <Field>
 *          placement for the profile fielddefs plus <ExtraFields /> for any future fields
 *          attached to the user model in metadata. Used by the User Manager's Profile
 *          card and by My Profile (ORCH-336; PRD User Manager UM-16).
 * Architecture: Declares modelId `user`, viewType `edit-modal`; the list fields (Country,
 *               Time Zone, Locale, Time Format, Language) resolve options via MDM like
 *               other template forms. Which fields follow Country is declared in metadata
 *               (`fields.default_from_*`, ORCH-410 §4.3) and handed in as `derivations`:
 *               a Country change refills each declared list field (Locale, Time Zone,
 *               Language)
 *               and prefills a declared text field (Phone's calling code) when it is
 *               empty or still holds the previous code. Locale filling Time Format is the
 *               one built-in step (its default comes from Intl, ORCH-413). The person can
 *               override any of them; storage stays NULL = follow and the card normalizes
 *               at save (utils/profileFormatting).
 *               Email is the account's identity, not a metadata field: the card passes it
 *               in. Company belongs to the membership, not the account (ORCH-410 §4.2):
 *               the page passes the tenant's CompanyField in. Order: First Name, Last Name
 *               / Email, Job Title / Company, Country / Phone, Time Zone / Locale, Time
 *               Format / Language, then the live formatting example full width (ORCH-382,
 *               ORCH-413, ORCH-410). Language is last: it is collected for later
 *               translation work and changes nothing yet.
 *               An invitee has no profile yet (ORCH-420): `variant: 'invite'` lays
 *               out only First Name, Last Name, Email and the Company; the person
 *               fills in the rest once they accept.
 */

import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import type { FormTemplate } from '../../components/template-form/types';
import { Section } from '../../components/template-form/layout';
import type { MasterDataValue } from '../../types/mdm';
import {
  COUNTRY_FIELD,
  LANGUAGE_FIELD,
  LOCALE_FIELD,
  TIME_FORMAT_FIELD,
  TIME_ZONE_FIELD,
  derivedDefault,
  isDerivedPrefill,
  localeHourCycleId,
  type DerivedDefault,
  type ProfileFormattingLists,
} from '../../utils/profileFormatting';

export interface UserProfileTemplateOptions {
  /** The read-only Email, placed after Last Name */
  email?: ReactNode;
  /** The membership's Company for the tenant being viewed, placed after Job Title (ORCH-410 §4.2) */
  company?: ReactNode;
  /** The live formatting example, placed full width under Locale / Time Format */
  formatExample?: ReactNode;
  /** The MDM lists the Country-derived defaults and the Locale → Time Format step read */
  lists?: ProfileFormattingLists;
  /** The derived defaults the view declares (`default_from_*`, ORCH-410 §4.3) */
  derivations?: DerivedDefault[];
  /** `invite`: the names, Email and Company only, for someone who hasn't accepted yet (ORCH-420) */
  variant?: 'profile' | 'invite';
}

export const COMMON_LOCALES = 'Common';
export const ALL_LOCALES = 'All locales';

/** Common locales first (the `fd_common-locale` flag on the value, ORCH-413), then the rest. */
export const localeGroup = (option: MasterDataValue): string =>
  option.meta_data?.['fd_common-locale'] === true ? COMMON_LOCALES : ALL_LOCALES;

/** A text field is prefilled with its derived value and a space, ready to type after. */
const prefilled = (value: string | null): string => (value ? `${value} ` : '');

/** The profile layout, with the caller's Email, Company and formatting example placed in it. */
export const buildUserProfileTemplate = ({
  email,
  company,
  formatExample,
  lists,
  derivations = [],
  variant = 'profile',
}: UserProfileTemplateOptions = {}): FormTemplate => {
  if (variant === 'invite') {
    return {
      modelId: 'user',
      viewType: 'edit-modal',
      render: ({ Field }) => (
        <Section columns={2}>
          <Field id="fd_first-name" />
          <Field id="fd_last-name" />
          {email}
          {company}
        </Section>
      ),
    };
  }
  return {
    modelId: 'user',
    viewType: 'edit-modal',
    hooks: lists && {
      // setValue fires this again, so a Country change flows through Locale into Time Format
      onFieldChange: (fieldDefId, value, form) => {
        derivations
          .filter((d) => d.sourceFieldId === fieldDefId)
          .forEach((d) => {
            const next = derivedDefault(d, value, lists);
            if (d.datatype === 'list') {
              form.setValue(d.fieldId, next ?? '');
              return;
            }
            // A Country change replaces an empty field or a bare prefill (a calling code
            // with nothing typed after it), never something the person typed
            const current = form.getValue(d.fieldId);
            const typed = typeof current === 'string' ? current.trim() : '';
            if (!typed || isDerivedPrefill(d, typed, lists)) {
              form.setValue(d.fieldId, prefilled(next));
            }
          });
        if (fieldDefId === LOCALE_FIELD) {
          form.setValue(
            TIME_FORMAT_FIELD,
            localeHourCycleId(value, lists.locales, lists.hourCycles) ?? ''
          );
        }
      },
    },
    render: ({ Field, ExtraFields }) => (
      <Section columns={2}>
        <Field id="fd_first-name" />
        <Field id="fd_last-name" />
        {email}
        <Field id="fd_job-title" />
        {company}
        <Field id={COUNTRY_FIELD} />
        <Field id="fd_user-phone" />
        <Field id={TIME_ZONE_FIELD} />
        <Field
          id={LOCALE_FIELD}
          requiredOverride
          componentProps={{
            disableClearable: true,
            groupBy: localeGroup,
            groupOrder: [COMMON_LOCALES, ALL_LOCALES],
          }}
        />
        <Field
          id={TIME_FORMAT_FIELD}
          requiredOverride
          componentProps={{ disableClearable: true }}
        />
        <Field id={LANGUAGE_FIELD} />
        {formatExample && <Box sx={{ gridColumn: '1 / -1' }}>{formatExample}</Box>}
        <ExtraFields columns={2} />
      </Section>
    ),
  };
};

export const CreateUserTemplate: FormTemplate = buildUserProfileTemplate();
